// src/api/users.js
import axios from "axios";
import { getAuthToken } from "../utils/storage";
import { API_BASE_URL } from "./apiClient";

/**
 * Create a new user (legacy; prefer invitations for normal onboarding).
 * @param {Object} userData
 * @returns {Promise<Object>}
 */
export const createUser = async (userData) => {
  const token = getAuthToken();
  const payload = {
    ...userData,
    roles: userData.roles.map((role) => {
      if (typeof role === "string") return role;
      if (typeof role === "object" && role.id) return role.id;
      return role;
    }),
  };
  const response = await axios.post(`${API_BASE_URL}/users/create`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  return response.data;
};

/**
 * Paginated user list. Always returns `{ items, total }`.
 * @param {Object} params
 * @param {number} [params.skip]
 * @param {number} [params.limit]
 * @param {string} [params.order] — reserved; not sent unless provided (backend ignores today)
 * @param {number|undefined} [params.tenant_id] — super-admin: filter by org (omit = all)
 */
export const getUsersList = async (params = {}) => {
  const token = getAuthToken();
  const query = {
    skip: params.skip ?? 0,
    limit: params.limit ?? 20,
  };
  if (params.search) query.search = params.search;
  if (params.tenant_id != null && params.tenant_id !== "") {
    query.tenant_id = params.tenant_id;
  }
  if (params.order != null && params.order !== "") {
    query.order = params.order;
  }
  const response = await axios.get(`${API_BASE_URL}/users/get-list`, {
    params: query,
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = response.data;
  if (Array.isArray(data)) {
    return { items: data, total: data.length };
  }
  return {
    items: data.items ?? [],
    total: typeof data.total === "number" ? data.total : (data.items?.length ?? 0),
  };
};

export const getUser = async (userId) => {
  const token = getAuthToken();
  const response = await axios.get(
    `${API_BASE_URL}/users/get-user/${userId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const assignRoles = async (userId, roleIds) => {
  const token = getAuthToken();
  const response = await axios.post(
    `${API_BASE_URL}/users/assign-roles`,
    { user_id: userId, role_ids: roleIds },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const updateUserRoles = async (userId, roleIds) => {
  const token = getAuthToken();
  const response = await axios.patch(
    `${API_BASE_URL}/users/${userId}/roles`,
    { role_ids: roleIds },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

/**
 * @param {number} userId
 * @param {Object} userData — partial allowed; include roles / tenant_id only when changing
 */
export const updateUser = async (userId, userData) => {
  const token = getAuthToken();
  const response = await axios.put(
    `${API_BASE_URL}/users/update-user/${userId}`,
    userData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const deactivateUser = async (userId) => {
  const token = getAuthToken();
  await axios.delete(`${API_BASE_URL}/users/delete-user/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

const userApi = {
  createUser,
  getUsersList,
  getUser,
  assignRoles,
  updateUserRoles,
  updateUser,
  deactivateUser,
};

export default userApi;
